/* Beegiv PDP · Personalize block behaviours (pet styling) */
(() => {
  const norm = (value) => String(value || '').trim().toLowerCase();
  const parseLines = (raw) => String(raw || '').split(/\r?\n|;/).map((line) => line.trim()).filter(Boolean);
  const escapeRe = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const init = (root) => {
    if (!root || root.dataset.pzReady) return;
    root.dataset.pzReady = 'true';
    const section = root.closest('[data-section-id]') || document;

    /* Variant option groups rendered by the section (Size / Fabric ...) */
    const groups = [...section.querySelectorAll('[data-option-group]')];
    const legendOf = (group) => group.closest('fieldset')?.querySelector('legend');
    const findGroup = (keyword) => groups.find((group) => norm(legendOf(group)?.textContent).startsWith(keyword));
    const sizeGroup = findGroup('size');
    const fabricGroup = findGroup('fabric');

    groups.forEach((group, index) => {
      const legend = legendOf(group);
      if (!legend || legend.querySelector('.pdp-pz__step-no')) return;
      const badge = document.createElement('span');
      badge.className = 'pdp-pz__step-no';
      badge.textContent = `Step ${index + 1}`;
      legend.prepend(badge);
    });

    /* Gallery helpers: open a guide image in the existing zoom dialog */
    const thumbs = [...section.querySelectorAll('[data-thumb-index]')];
    const dialog = section.querySelector('[data-gallery-dialog]');
    const dialogImage = section.querySelector('[data-gallery-dialog-image]');
    const findThumb = (re) => thumbs.find((thumb) => re.test(thumb.dataset.thumbAlt || ''));
    const openGuide = (thumb) => {
      if (!thumb || !dialog || !dialogImage || typeof dialog.showModal !== 'function') return;
      dialogImage.src = thumb.dataset.thumbSrc;
      dialogImage.alt = thumb.dataset.thumbAlt || '';
      dialog.showModal();
    };
    const addGuideLink = (group, label, re) => {
      const legend = legendOf(group);
      const thumb = findThumb(re);
      if (!legend || !thumb || legend.querySelector('.pdp-pz__guide')) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'pdp-pz__guide';
      button.textContent = label;
      button.addEventListener('click', () => openGuide(thumb));
      legend.append(button);
    };
    if (sizeGroup) addGuideLink(sizeGroup, 'Size guide', /size\s*(guide|chart)/i);
    if (fabricGroup) addGuideLink(fabricGroup, 'Fabric guide', /fabric\s*(guide|chart|swatch)/i);

    /* Size hints: 'XS|neck 9-11 in' (one per line or ; separated) */
    if (sizeGroup) {
      const hints = new Map(parseLines(root.dataset.sizeHints)
        .map((line) => line.split('|'))
        .filter((parts) => parts.length > 1)
        .map(([key, hint]) => [norm(key), hint.trim()]));
      sizeGroup.querySelectorAll('[data-option-value]').forEach((button) => {
        const hint = hints.get(norm(button.dataset.optionValue));
        if (!hint || button.querySelector('.pdp-pz__hint')) return;
        const small = document.createElement('small');
        small.className = 'pdp-pz__hint';
        small.textContent = hint;
        button.append(small);
        button.classList.add('has-hint');
      });
    }

    /* Fabric swatches taken from the variant images already on the page */
    const variantSelect = section.querySelector('[data-variant-select]');
    if (fabricGroup && variantSelect && root.dataset.fabricSwatches === 'true') {
      const fabricIndex = Number(fabricGroup.dataset.optionGroup);
      const options = [...variantSelect.options];
      fabricGroup.querySelectorAll('[data-option-value]').forEach((button) => {
        const match = options.find((option) => {
          if (!option.dataset.imageUrl) return false;
          try { return JSON.parse(option.dataset.options)[fabricIndex] === button.dataset.optionValue; } catch (_) { return false; }
        });
        if (!match) return;
        const url = match.dataset.imageUrl.replace(/([?&])width=\d+/, '$1width=180');
        button.style.setProperty('--pz-chip-img', `url('${url}')`);
        button.classList.add('has-swatch');
      });
    }

    /* Name -> live font samples; emoji can't be stitched */
    const nameInput = root.querySelector('[data-personalize-text]');
    const samples = [...root.querySelectorAll('[data-font-sample]')];
    const sampleDefault = root.dataset.fontSample || 'Max';
    const emojiTest = /\p{Extended_Pictographic}/u;
    const emojiStrip = /[\p{Extended_Pictographic}️‍]/gu;
    const syncSamples = () => {
      const text = (nameInput?.value || '').trim() || sampleDefault;
      samples.forEach((sample) => { sample.textContent = text; });
    };
    nameInput?.addEventListener('input', () => {
      if (emojiTest.test(nameInput.value)) {
        nameInput.value = nameInput.value.replace(emojiStrip, '');
        nameInput.dispatchEvent(new Event('input', { bubbles: true }));
        return;
      }
      syncSamples();
    });
    syncSamples();

    /* Checked-state class (fallback for browsers without :has) */
    const syncChecked = (selector, cardClass) => {
      root.querySelectorAll(selector).forEach((input) => {
        input.closest(`.${cardClass}`)?.classList.toggle('is-checked', input.checked);
      });
    };
    root.querySelectorAll('[data-font-input]').forEach((input) => {
      input.addEventListener('change', () => syncChecked('[data-font-input]', 'pdp-pz__font'));
    });
    syncChecked('[data-font-input]', 'pdp-pz__font');

    /* Thread colour: selected label, low-contrast guard, optional hero swap */
    const colorInputs = [...root.querySelectorAll('[data-color-input]')];
    const colorSelected = root.querySelector('[data-color-selected]');
    const colorNotice = root.querySelector('[data-color-notice]');
    const rules = new Map(parseLines(root.dataset.contrastRules)
      .map((line) => line.split('|'))
      .filter((parts) => parts.length > 1)
      .map(([fabric, colors]) => [norm(fabric), colors.split(',').map(norm).filter(Boolean)]));
    const currentFabric = () => fabricGroup?.querySelector('.is-active')?.dataset.optionValue || '';
    const showColorHero = (name) => {
      const thumb = findThumb(new RegExp(`${escapeRe(name)}\\s+thread`, 'i'));
      if (thumb) thumb.click();
    };
    const updateColorLabel = () => {
      const checked = colorInputs.find((input) => input.checked);
      if (colorSelected) colorSelected.textContent = checked ? checked.value : '';
      syncChecked('[data-color-input]', 'pdp-pz__color');
    };
    let lastFabric = null;
    const applyContrast = (announce) => {
      const fabric = currentFabric();
      const fabricChanged = fabric !== lastFabric;
      lastFabric = fabric;
      const blocked = rules.get(norm(fabric)) || [];
      const before = colorInputs.find((input) => input.checked);
      colorInputs.forEach((input) => {
        const bad = blocked.includes(norm(input.value));
        input.disabled = bad;
        input.closest('[data-color-option]')?.classList.toggle('is-blocked', bad);
      });
      let switched = null;
      if (before && before.disabled) {
        const next = colorInputs.find((input) => !input.disabled);
        if (next) {
          next.checked = true;
          switched = { from: before.value, to: next.value };
        }
      }
      updateColorLabel();
      if (colorNotice) {
        if (switched && announce) {
          colorNotice.textContent = `${switched.from} is hard to see on ${fabric}, so we switched you to ${switched.to}.`;
          colorNotice.hidden = false;
        } else if (fabricChanged && !switched) {
          colorNotice.hidden = true;
        }
      }
      if (switched) showColorHero(switched.to);
    };
    colorInputs.forEach((input) => {
      input.addEventListener('change', () => {
        updateColorLabel();
        if (colorNotice) colorNotice.hidden = true;
        showColorHero(input.value);
      });
    });
    fabricGroup?.addEventListener('click', (event) => {
      if (event.target.closest('[data-option-value]')) window.setTimeout(() => applyContrast(true), 0);
    });
    variantSelect?.addEventListener('change', () => applyContrast(true));
    applyContrast(false);

    /* Spelling confirmation: custom message instead of the native bubble */
    const confirmInput = root.querySelector('[data-confirm-input]');
    const confirmError = root.querySelector('[data-confirm-error]');
    const confirmWrap = root.querySelector('[data-confirm-wrap]');
    if (confirmInput) {
      confirmInput.addEventListener('invalid', (event) => {
        event.preventDefault();
        if (confirmError) {
          confirmError.textContent = confirmInput.dataset.requiredMessage || 'Please confirm you have checked the spelling.';
          confirmError.hidden = false;
        }
        confirmWrap?.classList.add('has-error');
      });
      confirmInput.addEventListener('change', () => {
        if (!confirmInput.checked) return;
        if (confirmError) confirmError.hidden = true;
        confirmWrap?.classList.remove('has-error');
      });
    }

    /* Photo upload: HEIC can't be previewed everywhere - show an 'attached' state instead */
    const uploadInput = root.querySelector('[data-upload-input]');
    const uploadPreview = root.querySelector('[data-upload-preview]');
    const uploadBox = uploadInput?.closest('.pdp__upload');
    uploadPreview?.addEventListener('error', () => {
      if (!uploadPreview.getAttribute('src')) return;
      uploadPreview.hidden = true;
      uploadBox?.classList.add('has-file-only');
    });
    uploadInput?.addEventListener('change', () => uploadBox?.classList.remove('has-file-only'));
    root.querySelector('[data-upload-remove]')?.addEventListener('click', () => uploadBox?.classList.remove('has-file-only'));
  };

  document.querySelectorAll('[data-personalize-root]').forEach(init);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-personalize-root]').forEach(init);
  });
})();
